import { useHandleResize } from "@hook";
import { clearParamWithoutRerender } from "@utils";
import { inject, observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { categoryImages, getCategoryGroupAlias, getCategoryGroupOffers, getCategoryGroups, getCategoryTitle } from "./categoryImages";
import List from "./List";
import { sendCategoryMetric } from "./metrics";
import "./style.scss";

const AB_3632 = inject("mainStore")(
    observer(({ mainStore }) => {
        const isMobile = useHandleResize();
        const categories = mainStore.getMainStore("offerCategories");
        const offersByCategory = mainStore.getMainStore("offersByCategory");
        const categoriesWithImages = getCategoryGroups(categories, offersByCategory);
        const selectedCategory = mainStore.getMainStore("selectedOfferCategory");
        const isStart = mainStore.getMainStore("isStart");
        const disableMetrika = new URLSearchParams(window.location.search).has("noym");
        const categoriesLoaded = offersByCategory.has("all");
        const showcaseMetricSentRef = useRef(false);
        const initialOfferIdRef = useRef(new URLSearchParams(window.location.search).get("id"));
        const deepLinkHandledRef = useRef(false);
        const categoryChipSelectionRef = useRef(null);
        const linkedOffer = (offersByCategory.get("all")?.offers ?? []).find((offer) => String(offer._id) === initialOfferIdRef.current);
        const selectedOffers = useMemo(() => getCategoryGroupOffers(selectedCategory, offersByCategory), [selectedCategory, offersByCategory]);
        const filtersRef = useRef(null);
        const dragStateRef = useRef({ isDown: false, startX: 0, scrollLeft: 0, hasMoved: false });
        const chipScrollRef = useRef({ userInitiated: false, metricSent: false, timer: null });

        const [scrollControls, setScrollControls] = useState({ previous: false, next: false });

        const selectCategory = (alias) => {
            deepLinkHandledRef.current = true;
            mainStore.setMainStore("selectedOfferCategory", alias);
            window.scrollTo({ top: 0, behavior: "smooth" });
        };

        const showCategories = () => {
            deepLinkHandledRef.current = true;
            clearParamWithoutRerender(["id", "code"]);
            sendCategoryMetric("click_select_category", mainStore);
            mainStore.setMainStore("selectedOfferCategory", null);
            window.scrollTo({ top: 0, behavior: "smooth" });
        };

        useEffect(() => {
            if (deepLinkHandledRef.current || selectedCategory || !linkedOffer) return;

            const group = (linkedOffer.categories || linkedOffer.vendorcategories || []).map((category) => getCategoryGroupAlias(category.alias)).find(Boolean);

            deepLinkHandledRef.current = true;
            if (group) mainStore.setMainStore("selectedOfferCategory", group);
        }, [linkedOffer, selectedCategory, mainStore]);

        useEffect(() => {
            const selectedFromChip = categoryChipSelectionRef.current === selectedCategory;
            categoryChipSelectionRef.current = null;
            if (!selectedFromChip && selectedCategory && Object.hasOwn(categoryImages, selectedCategory)) sendCategoryMetric("open_category", mainStore, getCategoryTitle({ alias: selectedCategory }));
        }, [selectedCategory, mainStore]);

        useEffect(() => {
            if (isStart || !categoriesLoaded || disableMetrika || showcaseMetricSentRef.current) return;

            showcaseMetricSentRef.current = true;
            sendCategoryMetric("show_showcase", mainStore);
        }, [isStart, categoriesLoaded, disableMetrika, mainStore]);

        useEffect(() => {
            chipScrollRef.current.userInitiated = false;
            chipScrollRef.current.metricSent = false;
            clearTimeout(chipScrollRef.current.timer);
            const activeFilter = filtersRef.current?.querySelector(".active");
            activeFilter?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
        }, [selectedCategory]);

        useEffect(() => () => clearTimeout(chipScrollRef.current.timer), []);

        const resetChipScrollAfterIdle = () => {
            const scroll = chipScrollRef.current;
            clearTimeout(scroll.timer);
            scroll.timer = setTimeout(() => {
                scroll.userInitiated = false;
                scroll.metricSent = false;
            }, 200);
        };

        const markUserChipScroll = () => {
            chipScrollRef.current.userInitiated = true;
            resetChipScrollAfterIdle();
        };

        const handleCategoryChipClick = (alias) => {
            sendCategoryMetric("click_category_chip", mainStore, getCategoryTitle({ alias }));
            if (alias !== selectedCategory) categoryChipSelectionRef.current = alias;
            selectCategory(alias);
        };

        const updateScrollControls = useCallback(() => {
            const filters = filtersRef.current;

            if (!filters) return;

            const maxScrollLeft = filters.scrollWidth - filters.clientWidth;
            const nextControls = {
                previous: filters.scrollLeft > 1,
                next: filters.scrollLeft < maxScrollLeft - 1,
            };

            setScrollControls((current) => (current.previous === nextControls.previous && current.next === nextControls.next ? current : nextControls));
        }, []);

        const handleFiltersScroll = () => {
            updateScrollControls();
            const scroll = chipScrollRef.current;

            if (!scroll.userInitiated) return;

            if (!scroll.metricSent) {
                scroll.metricSent = true;
                sendCategoryMetric("scroll_category_chips", mainStore);
            }

            resetChipScrollAfterIdle();
        };

        useEffect(() => {
            const frameId = requestAnimationFrame(updateScrollControls);

            window.addEventListener("resize", updateScrollControls);

            return () => {
                cancelAnimationFrame(frameId);
                window.removeEventListener("resize", updateScrollControls);
            };
        }, [isMobile, selectedCategory, categoriesWithImages.length, updateScrollControls]);

        const scrollFilters = (direction) => {
            const filters = filtersRef.current;

            if (isMobile || !filters) return;

            markUserChipScroll();
            filters.scrollBy({
                left: direction * Math.max(filters.clientWidth * 0.7, 240),
                behavior: "smooth",
            });
        };

        const handleMouseDown = (e) => {
            dragStateRef.current = {
                isDown: true,
                startX: e.pageX - filtersRef.current.offsetLeft,
                scrollLeft: filtersRef.current.scrollLeft,
                hasMoved: false,
            };
        };

        const handleMouseLeave = () => {
            dragStateRef.current.isDown = false;
        };

        const handleMouseUp = () => {
            dragStateRef.current.isDown = false;
        };

        const handleMouseMove = (e) => {
            if (!dragStateRef.current.isDown) return;

            e.preventDefault();
            const x = e.pageX - filtersRef.current.offsetLeft;
            const walk = x - dragStateRef.current.startX;

            if (Math.abs(walk) > 5) {
                dragStateRef.current.hasMoved = true;
                markUserChipScroll();
            }

            filtersRef.current.scrollLeft = dragStateRef.current.scrollLeft - walk;
        };

        const handleFiltersClickCapture = (e) => {
            if (!dragStateRef.current.hasMoved) return;

            e.preventDefault();
            e.stopPropagation();
            dragStateRef.current.hasMoved = false;
        };

        return (
            <div className={`ab-3632-categories ${selectedCategory ? "ab-3632-categories_selected" : ""}`}>
                {selectedCategory ? (
                    <>
                        <div className="ab-3632-categories__navigation">
                            <button className="ab-3632-categories__back" type="button" onClick={showCategories}>
                                <span aria-hidden="true">←</span>
                                <span>К выбору категорий</span>
                            </button>

                            <div className="ab-3632-categories__filters-wrap">
                                <button className="ab-3632-categories__filter ab-3632-categories__filter_all" type="button" onClick={() => mainStore.setMainStore("modal", { type: "categories-3632" })}>
                                    <span className="ab-3632-categories__filter-icon ab-3632-categories__filter-icon_menu" aria-hidden="true">
                                        <i />
                                        <i />
                                        <i />
                                    </span>
                                    <span>Все</span>
                                </button>

                                <div className={`ab-3632-categories__filters-scroll ${scrollControls.previous ? "ab-3632-categories__filters-scroll_fade-previous" : ""} ${scrollControls.next ? "ab-3632-categories__filters-scroll_fade-next" : ""}`}>
                                    <div
                                        className="ab-3632-categories__filters"
                                        ref={filtersRef}
                                        onClickCapture={isMobile ? undefined : handleFiltersClickCapture}
                                        onScroll={handleFiltersScroll}
                                        onWheel={markUserChipScroll}
                                        onTouchMove={markUserChipScroll}
                                        onKeyDown={(event) => {
                                            if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) markUserChipScroll();
                                        }}
                                        onMouseDown={isMobile ? undefined : handleMouseDown}
                                        onMouseLeave={isMobile ? undefined : handleMouseLeave}
                                        onMouseUp={isMobile ? undefined : handleMouseUp}
                                        onMouseMove={isMobile ? undefined : handleMouseMove}
                                    >
                                        {categoriesWithImages.map((category) => (
                                            <button className={`ab-3632-categories__filter ${selectedCategory === category.alias ? "active" : ""}`} type="button" key={category.alias} onClick={() => handleCategoryChipClick(category.alias)}>
                                                {categoryImages[category.alias] ? <img className="ab-3632-categories__filter-icon" src={categoryImages[category.alias]}></img> : null}
                                                <span>{getCategoryTitle(category)}</span>
                                            </button>
                                        ))}
                                    </div>

                                    {!isMobile && scrollControls.previous ? (
                                        <button className="ab-3632-categories__filters-chevron ab-3632-categories__filters-chevron_previous" type="button" aria-label="Прокрутить категории назад" onClick={() => scrollFilters(-1)}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="27" height="28" viewBox="0 0 27 28" fill="none">
                                                <path
                                                    d="M9.45929 13.29L15.1193 7.64004C15.2122 7.54631 15.3228 7.47191 15.4447 7.42115C15.5666 7.37038 15.6973 7.34424 15.8293 7.34424C15.9613 7.34424 16.092 7.37038 16.2139 7.42115C16.3357 7.47191 16.4463 7.54631 16.5393 7.64004C16.7255 7.8274 16.8301 8.08085 16.8301 8.34504C16.8301 8.60922 16.7255 8.86267 16.5393 9.05004L11.5893 14.05L16.5393 19C16.7255 19.1874 16.8301 19.4409 16.8301 19.705C16.8301 19.9692 16.7255 20.2227 16.5393 20.41C16.4467 20.5045 16.3362 20.5797 16.2144 20.6312C16.0925 20.6827 15.9616 20.7095 15.8293 20.71C15.697 20.7095 15.5661 20.6827 15.4442 20.6312C15.3223 20.5797 15.2119 20.5045 15.1193 20.41L9.45929 14.76C9.35778 14.6664 9.27677 14.5527 9.22136 14.4262C9.16596 14.2997 9.13735 14.1631 9.13735 14.025C9.13735 13.8869 9.16596 13.7503 9.22136 13.6238C9.27677 13.4973 9.35778 13.3837 9.45929 13.29Z"
                                                    fill="#111111"
                                                />
                                            </svg>
                                        </button>
                                    ) : null}

                                    {!isMobile && scrollControls.next ? (
                                        <button className="ab-3632-categories__filters-chevron ab-3632-categories__filters-chevron_next" type="button" aria-label="Прокрутить категории вперёд" onClick={() => scrollFilters(1)}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="27" height="28" viewBox="0 0 27 28" fill="none">
                                                <path
                                                    d="M17.5407 13.29L11.8807 7.64004C11.7878 7.54631 11.6772 7.47191 11.5553 7.42115C11.4334 7.37038 11.3027 7.34424 11.1707 7.34424C11.0387 7.34424 10.908 7.37038 10.7861 7.42115C10.6643 7.47191 10.5537 7.54631 10.4607 7.64004C10.2745 7.8274 10.1699 8.08085 10.1699 8.34504C10.1699 8.60922 10.2745 8.86267 10.4607 9.05004L15.4107 14.05L10.4607 19C10.2745 19.1874 10.1699 19.4409 10.1699 19.705C10.1699 19.9692 10.2745 20.2227 10.4607 20.41C10.5533 20.5045 10.6638 20.5797 10.7856 20.6312C10.9075 20.6827 11.0384 20.7095 11.1707 20.71C11.303 20.7095 11.4339 20.6827 11.5558 20.6312C11.6777 20.5797 11.7881 20.5045 11.8807 20.41L17.5407 14.76C17.6422 14.6664 17.7232 14.5527 17.7786 14.4262C17.834 14.2997 17.8626 14.1631 17.8626 14.025C17.8626 13.8869 17.834 13.7503 17.7786 13.6238C17.7232 13.4973 17.6422 13.3837 17.5407 13.29Z"
                                                    fill="#111111"
                                                />
                                            </svg>
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                        <List key={selectedCategory} offersList={selectedOffers}></List>
                    </>
                ) : (
                    <div className="ab-3632-categories__container">
                        {categoriesWithImages.map((category) => {
                            const image = categoryImages[category.alias];

                            return (
                                <div className="ab-3632-categories__card" data-category={category.alias} type="button" key={category.alias} onClick={() => selectCategory(category.alias)}>
                                    <div className="ab-3632-categories__card-top">
                                        <span className="ab-3632-categories__title">{getCategoryTitle(category)}</span>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M14.4297 5.92993L20.4997 11.9999L14.4297 18.0699" stroke="#111111" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M3.5 12H20.33" stroke="#111111" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>

                                    {image ? <img className="ab-3632-categories__image" src={image} alt="" /> : null}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }),
);

export default AB_3632;
